import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth, parseFqnToPathParts } from '@/shared/lib/format/fqn';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import type { RelatedTermEditHook, RelatedTermTreeNode } from '@/shared/types/editHooks';
import type { TreeSelectNode } from '@/shared/ui/EditableTreeSelect';

import type { GlossaryCategoryItem } from '@/entities/glossary';

import type { GlossaryTermDetail } from '@/features/glossary/api';
import { getGlossaryCategoryApi } from '@/features/glossary/api/getGlossaryCategoryListApi';
import { getGlossaryTermTreeApi } from '@/features/glossary/api/getGlossaryTermApi';
import { updateGlossaryTermRelatedTerms } from '@/features/glossary/api/updateGlossaryTermRelatedTerms';

const convertTermsToTree = (
  categories: GlossaryCategoryItem[],
  terms: Array<{
    id: string;
    fullyQualifiedName: string;
    displayName?: string;
    name: string;
    description?: string;
    childrenCount?: number;
  }>,
  currentTermFqn?: string
): RelatedTermTreeNode[] => {
  if (!Array.isArray(categories) || !Array.isArray(terms)) {
    return [];
  }

  const termsByFqn = new Map<string, (typeof terms)[0]>();
  terms.forEach((term) => {
    termsByFqn.set(term.fullyQualifiedName, term);
  });

  const buildTermChildren = (parentFqn: string): RelatedTermTreeNode[] => {
    const children = terms
      .filter((term) => {
        const parts = parseFqnToPathParts(term.fullyQualifiedName);
        const parentParts = parseFqnToPathParts(parentFqn);
        if (parts.length !== parentParts.length + 1) return false;
        return parentParts.every((part, index) => part === parts[index]);
      })
      .sort((a, b) => {
        const aLabel = a.displayName || a.name;
        const bLabel = b.displayName || b.name;
        return aLabel.localeCompare(bLabel);
      });

    return children.map((term) => {
      const depth = calculateFqnDepth(term.fullyQualifiedName);

      const hasNoChildren = !term.childrenCount;
      const isMaxDepth = depth >= TREE_DEPTH.TREE_SELECT;
      const isLeaf = hasNoChildren || isMaxDepth;
      const isCurrentTerm = currentTermFqn === term.fullyQualifiedName;

      const termChildren = isMaxDepth ? [] : buildTermChildren(term.fullyQualifiedName);

      return {
        key: term.id,
        value: term.id,
        title: term.displayName || term.name,
        isLeaf,
        fullyQualifiedName: term.fullyQualifiedName,
        description: term.description,
        disabled: isCurrentTerm,
        disableCheckbox: isCurrentTerm,
        children: termChildren.length > 0 ? termChildren : undefined,
      };
    });
  };

  const treeData: RelatedTermTreeNode[] = [];

  categories.forEach((category) => {
    const categoryChildren = buildTermChildren(category.fullyQualifiedName);

    treeData.push({
      key: `glossary-${category.fullyQualifiedName}`,
      value: `glossary-${category.fullyQualifiedName}`,
      title: category.displayName || category.name,
      description: category.description,
      selectable: false,
      disabled: true,
      disableCheckbox: true,
      children: categoryChildren.length > 0 ? categoryChildren : undefined,
      isLeaf: categoryChildren.length === 0,
    });
  });

  return treeData.sort((a, b) => {
    const aTitle = typeof a.title === 'string' ? a.title : '';
    const bTitle = typeof b.title === 'string' ? b.title : '';
    return aTitle.localeCompare(bTitle);
  });
};

const convertRelatedTermTreeToTreeSelectNode = (nodes: RelatedTermTreeNode[]): TreeSelectNode[] => {
  return nodes.map((node) => ({
    value: node.value,
    title: node.title,
    key: node.key,
    label: typeof node.title === 'string' ? node.title : undefined,
    children: node.children ? convertRelatedTermTreeToTreeSelectNode(node.children) : undefined,
    disabled: node.disabled,
    selectable: node.selectable,
    description: node.description,
    isLeaf: node.isLeaf,
  }));
};

type UseGlossaryTermRelatedTermEditParams = {
  termId: string;
  termFqn: string;
};

export const useGlossaryTermRelatedTermEdit = ({
  termId,
  termFqn,
}: UseGlossaryTermRelatedTermEditParams): RelatedTermEditHook => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTermIds, setSelectedTermIds] = useState<string[]>([]);

  const queryKey = useMemo(() => glossaryKeys.termDetailByFqn(termFqn), [termFqn]);

  const termData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
  const currentRelatedTerms = useMemo(() => termData?.relatedTerms || [], [termData?.relatedTerms]);

  const { data: categoriesData, isLoading: isLoadingCategories } = useQueryWithBoundary({
    queryKey: glossaryKeys.categoriesAll(),
    queryFn: async () => {
      const response = await getGlossaryCategoryApi({ limit: 1000 });
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const { data: allTermsData, isLoading: isLoadingTermsData } = useQueryWithBoundary({
    queryKey: glossaryKeys.termsAll(),
    queryFn: async () => {
      const response = await getGlossaryTermTreeApi({ limit: 10000 });
      return response.data || [];
    },
    staleTime: 5 * 60 * 1000,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const categories = useMemo(() => (Array.isArray(categoriesData) ? categoriesData : []), [categoriesData]);
  const allTerms = useMemo(() => (Array.isArray(allTermsData) ? allTermsData : []), [allTermsData]);

  const relatedTermTreeData = useMemo(() => {
    const treeData =
      categories.length > 0 || allTerms.length > 0 ? convertTermsToTree(categories, allTerms, termFqn) : [];
    return convertRelatedTermTreeToTreeSelectNode(treeData);
  }, [categories, allTerms, termFqn]);

  const currentRelatedTermIds = useMemo(() => currentRelatedTerms.map((t) => t.id), [currentRelatedTerms]);

  const relatedTermTreeSelectValue = useMemo(() => {
    return selectedTermIds.map((termId) => {
      const term = currentRelatedTerms.find((t) => t.id === termId);
      return {
        value: termId,
        label: term ? term.displayName || term.name : termId,
      };
    });
  }, [selectedTermIds, currentRelatedTerms]);

  useEffect(() => {
    setSelectedTermIds(currentRelatedTermIds);
  }, [currentRelatedTermIds]);

  const updateRelatedTermsMutation = useMutation({
    mutationFn: async (newTermIds: string[]) => {
      await updateGlossaryTermRelatedTerms(termId, newTermIds, allTerms, currentRelatedTerms);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      notification.error({
        message: NOTIFICATION_MESSAGES.TERM_UPDATE_ERROR || '관련 용어 업데이트 실패',
        description: error instanceof Error ? error.message : '관련 용어 업데이트에 실패했습니다.',
        duration: 5,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      notification.success({
        message: NOTIFICATION_MESSAGES.TERM_UPDATE_SUCCESS || '관련 용어 업데이트 성공',
        description: NOTIFICATION_MESSAGES.TERM_UPDATE_SUCCESS_DESC || '관련 용어가 성공적으로 업데이트되었습니다.',
        placement: 'bottomLeft',
        duration: 5,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestRelatedTermIds = (latestTermData?.relatedTerms || []).map((t) => t.id);
    setSelectedTermIds(latestRelatedTermIds);
  }, [queryClient, queryKey]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestRelatedTermIds = (latestTermData?.relatedTerms || []).map((t) => t.id);
    setSelectedTermIds(latestRelatedTermIds);
  }, [queryClient, queryKey]);

  const handleUpdate = useCallback(
    (newTermIds: string[]) => {
      updateRelatedTermsMutation.mutate(newTermIds, {
        onSuccess: () => {
          setIsOpen(false);
        },
      });
    },
    [updateRelatedTermsMutation]
  );

  const isLoadingTerms = useMemo(
    () => isLoadingCategories || isLoadingTermsData,
    [isLoadingCategories, isLoadingTermsData]
  );

  const relatedBadgeItems = useMemo(
    () =>
      currentRelatedTerms.map((term) => ({
        tagFQN: term.id,
        name: term.name,
        displayName: term.displayName || term.name,
        onClick: () => {
          navigate(buildAssetDetailUrl('glossaryTerm', term.fullyQualifiedName));
        },
      })),
    [currentRelatedTerms, navigate]
  );

  const handleSubmit = useCallback(
    async (selectedTermIds: string[]) => {
      handleUpdate(selectedTermIds);
    },
    [handleUpdate]
  );

  return {
    isOpen,
    relatedTermTreeData,
    relatedTermTreeSelectValue,
    isLoadingTerms,
    isUpdating: updateRelatedTermsMutation.isPending,
    relatedBadgeItems,
    handleSubmit,
    handleOpen,
    handleClose,
  };
};
